import { z } from 'zod';
import { DEPARTMENT_DEFS } from './hr.js';
import {
  CHECK_METHODS,
  CHECK_METHOD_MAP,
  MAX_DEDUCTION_RULES,
  ORG_UNIT_KINDS,
  WORK_MODES,
  overlappingRules,
} from './orgsetup.js';

/** مخططات إعداد المؤسسة. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const DEPARTMENT_KEYS_TUPLE = DEPARTMENT_DEFS.map((d) => d.key) as unknown as [
  string,
  ...string[],
];

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const timeOnly = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'الوقت بصيغة ساعة:دقيقة.');

/* ==========================================================================
   مراكز التكلفة
   ========================================================================== */

export const UpsertCostCenterSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'كود المركز مطلوب.')
    .max(20, 'الكود أطول من عشرين حرفًا.'),
  name: z.string().trim().min(2, 'اسم المركز قصير.').max(120),
  notes: optionalText(1000),
  isActive: z.boolean().default(true),
});
export type UpsertCostCenterInput = z.infer<typeof UpsertCostCenterSchema>;

/* ==========================================================================
   الوحدات التنظيمية
   ========================================================================== */

export const UpsertOrgUnitSchema = z.object({
  code: optionalText(20),
  name: z.string().trim().min(2, 'اسم الوحدة قصير.').max(120),
  kind: z.enum(ORG_UNIT_KINDS).default('DEPARTMENT'),
  parentId: Cuid.nullable().optional(),
  /**
   * قسمُ النظام.
   *
   * ليس تصنيفًا للعرض: به يُعرف أين يظهر من يتبع هذه الوحدة — في فريق
   * المبيعات أو في مشاريع الوكالة أو في مركز الاتصال. ووحدةٌ بلا مفتاحٍ
   * تُبقي من فيها خارج كلّ فريق.
   */
  deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
  managerId: Cuid.nullable().optional(),
  costCenterId: Cuid.nullable().optional(),
  notes: optionalText(1000),
  order: z.coerce.number().int().min(0).max(999).default(0),
  isActive: z.boolean().default(true),
});
export type UpsertOrgUnitInput = z.infer<typeof UpsertOrgUnitSchema>;

/* ==========================================================================
   الورديات
   ========================================================================== */

const deductionRule = z.object({
  fromMinutes: z.coerce.number().int().min(0).max(1440),
  /** فارغةٌ تعني شريحةً مفتوحةَ الطرف: «أكثر من ساعتين». */
  toMinutes: z.coerce.number().int().min(1).max(1441).nullable(),
  deductionDays: z.coerce.number().min(0).max(30),
});

const deductionRules = z
  .array(deductionRule)
  .max(MAX_DEDUCTION_RULES, 'شرائحُ أكثرُ من أن تُقرأ.')
  .default([])
  .refine(
    (rules) => rules.every((r) => r.toMinutes === null || r.toMinutes > r.fromMinutes),
    { message: 'نهايةُ الشريحة قبل بدايتها.' },
  )
  .refine((rules) => overlappingRules(rules) === null, {
    message: 'شريحتان متداخلتان — يختلف الخصمُ باختلاف الترتيب وحدَه.',
  });

/**
 * خطّةُ عمل: ورديّةٌ ومعها لائحةُ جزاءاتها.
 *
 * والعابرةُ لمنتصف الليل تُعلَن صراحةً ولا تُستنتج من «النهايةُ أصغر»:
 * ورديّةٌ من التاسعة صباحًا إلى التاسعة صباحًا — أربعٌ وعشرون ساعةً —
 * تبدو للاستنتاج عابرةً وهي خطأُ إدخال.
 */
export const UpsertScheduleSchema = z
  .object({
    name: z.string().trim().min(2, 'اسم الخطة قصير.').max(120),
    workDays: z
      .array(z.coerce.number().int().min(0).max(6))
      .min(1, 'يوم عملٍ واحدٌ على الأقلّ.')
      .max(7),
    startTime: timeOnly,
    endTime: timeOnly,
    breakMinutes: z.coerce.number().int().min(0).max(480).default(60),
    graceMinutes: z.coerce.number().int().min(0).max(240).default(15),
    flexibleMinutes: z.coerce.number().int().min(0).max(480).default(0),
    breakStart: timeOnly.nullable().optional(),
    breakEnd: timeOnly.nullable().optional(),
    overnight: z.boolean().default(false),

    /* ---- الجزاءات والبدلات ---- */
    absenceDeductionDays: z.coerce.number().min(0).max(30).default(1),
    forgotCheckoutDays: z.coerce.number().min(0).max(30).default(0),
    overtimeRate: z.coerce.number().min(0).max(10).default(1),
    holidayRate: z.coerce.number().min(0).max(10).default(2),
    lateRules: deductionRules,
    earlyRules: deductionRules,

    /* ---- التسجيل ---- */
    methods: z
      .array(z.enum(CHECK_METHODS))
      .min(1, 'طريقةُ تسجيلٍ واحدةٌ على الأقلّ.')
      .default(['WEB'])
      .refine((m) => m.every((k) => CHECK_METHOD_MAP[k].built), {
        message: 'طريقةٌ لم تُبنَ بعد — لا تُختار حتى تُبنى.',
      }),
    requireGps: z.boolean().default(true),

    isDefault: z.boolean().default(false),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.overnight || v.endTime > v.startTime, {
    message: 'نهاية الوردية قبل بدايتها — علّمها «تعبر منتصف الليل» إن كانت كذلك.',
    path: ['endTime'],
  })
  .refine(
    (v) => new Set(v.workDays).size === v.workDays.length,
    { message: 'يومٌ مكرّرٌ في أيّام العمل.', path: ['workDays'] },
  )
  .refine(
    (v) => !v.breakStart === !v.breakEnd,
    { message: 'موعدُ الراحة يحتاج بدايةً ونهاية.', path: ['breakEnd'] },
  )
  .refine(
    (v) => !v.breakStart || !v.breakEnd || v.breakEnd > v.breakStart,
    { message: 'نهايةُ الراحة قبل بدايتها.', path: ['breakEnd'] },
  );
export type UpsertScheduleInput = z.infer<typeof UpsertScheduleSchema>;

/* ==========================================================================
   ما يُسند إلى الموظّف
   ========================================================================== */

export const AssignOrgSchema = z.object({
  orgUnitId: Cuid.nullable().optional(),
  costCenterId: Cuid.nullable().optional(),
  scheduleId: Cuid.nullable().optional(),
  workMode: z.enum(WORK_MODES).optional(),
});
export type AssignOrgInput = z.infer<typeof AssignOrgSchema>;

/* ==========================================================================
   ما يُحرَّك بالسحب في الهيكل
   ========================================================================== */

/**
 * نقلُ وحدةٍ تحت أخرى.
 *
 * مخطَّطٌ صغيرٌ مستقلٌّ لا `UpsertOrgUnitSchema`: السحبُ يغيّر الأبَ
 * والترتيبَ وحدَهما، وإرسالُ الوحدة كاملةً في كلّ إفلاتٍ يجعل حقلًا
 * أغفله العميلُ يُمحى — وهو ما وقع فعلًا في شاشاتٍ أخرى.
 */
export const MoveUnitSchema = z.object({
  /** فارغٌ يعني جذرًا: خرجت من تحت الجميع. */
  parentId: Cuid.nullable(),
  order: z.coerce.number().int().min(0).max(999).optional(),
});
export type MoveUnitInput = z.infer<typeof MoveUnitSchema>;

/** وضعُ مسمًّى وظيفيٍّ في وحدة — أو إخراجُه منها. */
export const PlacePositionSchema = z.object({
  orgUnitId: Cuid.nullable(),
});
export type PlacePositionInput = z.infer<typeof PlacePositionSchema>;
